import React from "react";
import { Link } from "react-router-dom";
import { HeartPulse, Coffee, Bed, ShieldCheck, Sparkles, Music } from "lucide-react";
import { IMAGES } from "../lib/site";
import { PageHero, SectionHeading } from "../components/Sections";

const amenities = [
  { icon: Bed, title: "Couture Suites", desc: "Private suites with silk linens, ambient lighting and curated art." },
  { icon: HeartPulse, title: "24/7 Nursing", desc: "Licensed RNs on-site, with post-op monitoring and gentle attentiveness." },
  { icon: Sparkles, title: "Lymphatic Massage", desc: "Daily certified lymphatic drainage to refine sculpting and reduce swelling." },
  { icon: Coffee, title: "Nutrition Concierge", desc: "Chef-prepared, surgeon-approved meals designed for healing and beauty." },
  { icon: Music, title: "Sanctuary Ambience", desc: "Aromatherapy, sound healing and meditative playlists curated in-house." },
  { icon: ShieldCheck, title: "Medical Security", desc: "Discreet, key-coded entry and confidential guest protocols." },
];

export default function RecoveryPage() {
  return (
    <div data-testid="page-recovery">
      <PageHero
        kicker="The Recovery Center"
        title="Heal in a"
        accent="private sanctuary"
        subtitle="Our medical recovery center is reimagined as a serene resort — designed to make post-operative care feel like a curated retreat."
        image={IMAGES.recovery}
        testid="recovery-hero"
      />

      <section className="py-24 md:py-32 bg-mag">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <SectionHeading
              kicker="The Experience"
              title="Where rest becomes"
              accent="restoration"
              testid="recovery-exp-heading"
            />
            <p className="text-white/75 font-heading text-lg mt-8 leading-relaxed">
              Recovery has long been the overlooked chapter of cosmetic
              surgery. We've rewritten it. At our medical recovery center,
              your healing days are choreographed with the same precision
              as the procedure itself — only with softer lighting and a
              quieter pace.
            </p>
            <p className="text-white/65 font-heading text-lg mt-4 leading-relaxed">
              Your stay includes round-the-clock medical care, certified
              lymphatic drainage, surgeon-supervised garment fittings, and a
              meal program built to accelerate healing while honouring your
              palate.
            </p>
          </div>
          <div className="md:col-span-6">
            <img src={IMAGES.recovery} alt="" className="w-full h-[520px] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-24 md:py-28 bg-mag-2">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading
            kicker="Amenities"
            title="Every detail,"
            accent="composed"
            align="center"
            testid="recovery-amenities-heading"
          />
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {amenities.map((a, i) => (
              <div key={i} className="mag-card p-8" data-testid={`recovery-amenity-${i}`}>
                <a.icon size={26} className="text-gold mb-5" />
                <h4 className="font-heading text-2xl text-white mb-2">{a.title}</h4>
                <p className="text-white/65 text-[15px] leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-mag">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h3 className="font-heading text-4xl md:text-5xl text-white">
            Tour our{" "}
            <span className="font-brand text-pink-luxe text-5xl md:text-6xl">recovery atelier</span>
          </h3>
          <p className="text-white/65 font-heading text-lg mt-6 max-w-2xl mx-auto">
            Private tours are available by appointment. Reserve a viewing
            during your surgical consultation.
          </p>
          <Link to="/consultations" className="btn-gold mt-8" data-testid="recovery-cta">
            Schedule a Tour
          </Link>
        </div>
      </section>
    </div>
  );
}
